import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { TipTapEditor } from '../../components/admin/TipTapEditor';
import { API_BASE } from '../../config/api';

export const PageForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    seo_title: '',
    seo_description: '',
    featured_image: '',
    content: {},
    ad_banners: [{ image: '', target_url: '' }],
    ad_banners_enabled: true,
    published: false
  });

  const backendUrl = API_BASE;

  useEffect(() => {
    if (isEdit) {
      fetchPage();
    }
  }, [id]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!isEdit && formData.title && !formData.slug) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.title, isEdit]);

  const fetchPage = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${backendUrl}/api/admin/pages/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch page');

      const data = await response.json();
      setFormData({
        title: data.page.title || '',
        slug: data.page.slug || '',
        seo_title: data.page.seo_title || '',
        seo_description: data.page.seo_description || '',
        featured_image: data.page.featured_image || '',
        content: data.page.content || {},
        ad_banners: data.page.ad_banners && data.page.ad_banners.length > 0
          ? data.page.ad_banners
          : [{ image: '', target_url: '' }],
        ad_banners_enabled: data.page.ad_banners_enabled !== false,
        published: data.page.published || false
      });
    } catch (err) {
      alert('Failed to load page');
      navigate('/admin/pages');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('admin_token');
      const url = isEdit
        ? `${backendUrl}/api/admin/pages/${id}`
        : `${backendUrl}/api/admin/pages`;

      // Filter out empty ad banners
      const payload = {
        ...formData,
        ad_banners: formData.ad_banners.filter(
          banner => (banner.image && typeof banner.image === 'string' && banner.image.trim() !== '') || (banner.target_url && typeof banner.target_url === 'string' && banner.target_url.trim() !== '')
        )
      };

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Save failed:', errorData);
        throw new Error('Failed to save');
      }

      navigate('/admin/pages');
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const addAdBanner = () => {
    if (formData.ad_banners.length >= 3) {
      alert('Maximum 3 ad banners allowed');
      return;
    }
    setFormData(prev => ({
      ...prev,
      ad_banners: [...prev.ad_banners, { image: '', target_url: '' }]
    }));
  };

  const removeAdBanner = (index) => {
    setFormData(prev => ({
      ...prev,
      ad_banners: prev.ad_banners.filter((_, i) => i !== index)
    }));
  };

  const updateAdBanner = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ad_banners: prev.ad_banners.map((banner, i) =>
        i === index ? { ...banner, [field]: value } : banner
      )
    }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-[#1E40AF] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-[#6B7280]">Loading page...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate('/admin/pages')}
            className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm flex items-center gap-2 mb-3"
            data-testid="back-to-pages"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Pages
          </button>
          <h1 className="text-3xl font-bold text-[#111827]" data-testid="page-form-title">
            {isEdit ? 'Edit' : 'Create'} Page
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white border-2 border-[#E5E7EB] rounded-xl p-6 space-y-6" data-testid="page-form">
          {/* Basic Information */}
          <div>
            <h2 className="text-lg font-bold text-[#111827] mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#111827] mb-2">Title *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="e.g., About Us, Privacy Policy, Contact"
                  data-testid="input-title"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#111827] mb-2">Slug *</label>
                <div className="flex items-center">
                  <span className="px-4 py-2 bg-[#F8FAFC] border-2 border-r-0 border-[#E5E7EB] rounded-l-lg text-[#6B7280] text-sm">
                    /pages/
                  </span>
                  <input
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    className="flex-1 px-4 py-2 border-2 border-[#E5E7EB] rounded-r-lg focus:outline-none focus:border-[#1E40AF]"
                    placeholder="about-us"
                    data-testid="input-slug"
                  />
                </div>
                <p className="text-xs text-[#6B7280] mt-1">Auto-generated from title. Use lowercase letters, numbers, and hyphens only.</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-[#111827] mb-2">Featured Image URL</label>
                <input
                  type="url"
                  value={formData.featured_image}
                  onChange={(e) => setFormData({...formData, featured_image: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="https://..."
                  data-testid="input-featured-image"
                />
                <p className="text-xs text-[#6B7280] mt-1">Recommended: 1920x600px for full-width hero display</p>
              </div>
            </div>
          </div>

          {/* Page Content */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Page Content</h2>
            <TipTapEditor
              content={formData.content}
              onChange={(data) => setFormData({...formData, content: data})}
              placeholder="Start writing your page content..."
            />
            <p className="text-xs text-[#6B7280] mt-2">Full rich text editor: headings, lists, images, links, and more.</p>
          </div>

          {/* Ad Banners */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-[#111827]">Ad Banners (Right Sidebar)</h2>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.ad_banners_enabled}
                    onChange={(e) => setFormData({...formData, ad_banners_enabled: e.target.checked})}
                    className="w-4 h-4"
                    data-testid="toggle-ad-banners"
                  />
                  <span className="text-sm font-medium text-[#6B7280]">Enable</span>
                </label>
              </div>
            </div>
            <p className="text-xs text-[#6B7280] mb-4">Add up to 3 ad banners. Displayed on desktop (right side) and mobile (below content). Recommended size: 300x300px (square)</p>
            
            <div className="space-y-4">
              {formData.ad_banners.map((banner, index) => (
                <div key={index} className="border-2 border-[#E5E7EB] rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold text-[#111827]">Banner {index + 1}</h3>
                    {formData.ad_banners.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeAdBanner(index)}
                        className="text-red-600 hover:text-red-800 text-sm font-semibold"
                        data-testid={`remove-banner-${index}`}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-[#6B7280] mb-1">Image URL</label>
                      <input
                        type="url"
                        value={banner.image}
                        onChange={(e) => updateAdBanner(index, 'image', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                        placeholder="https://... (300x300px recommended)"
                        data-testid={`banner-image-${index}`}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#6B7280] mb-1">Target URL (click destination)</label>
                      <input
                        type="url"
                        value={banner.target_url}
                        onChange={(e) => updateAdBanner(index, 'target_url', e.target.value)}
                        className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                        placeholder="https://..."
                        data-testid={`banner-url-${index}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
              
              {formData.ad_banners.length < 3 && (
                <button
                  type="button"
                  onClick={addAdBanner}
                  className="text-[#1E40AF] hover:text-[#1e3a8a] font-semibold text-sm flex items-center gap-2"
                  data-testid="add-banner-button"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Banner ({formData.ad_banners.length}/3)
                </button>
              )}
            </div>
          </div>

          {/* SEO Fields */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">SEO Settings</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">SEO Title</label>
                <input
                  type="text"
                  value={formData.seo_title}
                  onChange={(e) => setFormData({...formData, seo_title: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="Custom title for search engines (defaults to page title)"
                  maxLength={60}
                  data-testid="input-seo-title"
                />
                <p className="text-xs text-[#6B7280] mt-1">{formData.seo_title.length}/60 characters</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#111827] mb-2">SEO Description</label>
                <textarea
                  value={formData.seo_description}
                  onChange={(e) => setFormData({...formData, seo_description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border-2 border-[#E5E7EB] rounded-lg focus:outline-none focus:border-[#1E40AF]"
                  placeholder="Brief description for search engine results"
                  maxLength={160}
                  data-testid="input-seo-description"
                />
                <p className="text-xs text-[#6B7280] mt-1">{formData.seo_description.length}/160 characters</p>
              </div>
            </div>
          </div>

          {/* Publish Options */}
          <div className="pt-6 border-t-2 border-[#E5E7EB]">
            <h2 className="text-lg font-bold text-[#111827] mb-4">Publish Settings</h2>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({...formData, published: e.target.checked})}
                className="w-5 h-5"
                data-testid="checkbox-published"
              />
              <label htmlFor="published" className="text-sm font-semibold text-[#111827] cursor-pointer">
                Publish (make visible on website)
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-6">
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-3 bg-gradient-to-r from-[#1E40AF] to-[#1e3a8a] hover:shadow-xl text-white font-semibold rounded-lg transition-all disabled:opacity-50"
              data-testid="submit-button"
            >
              {saving ? 'Saving...' : (isEdit ? 'Update' : 'Create')} Page
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/pages')}
              className="px-8 py-3 bg-white border-2 border-[#E5E7EB] text-[#111827] hover:border-[#1E40AF] rounded-lg font-semibold transition-all"
              data-testid="cancel-button"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default PageForm;
